import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API_URL from '../config/api';
import useSEO from '../hooks/useSEO';
import PaymentMethods from '../components/PaymentMethods';
import Footer from '../components/Footer';

function Account() {
  useSEO({
    title: 'My Account | Nightfall',
    description: 'Manage your Nightfall account, view purchased games, and update your profile.',
    url: 'https://nightfallshop.com/account'
  });
  const navigate = useNavigate();
  const { user, isAuthenticated, logout, loading, refreshUser, token, updateToken } = useAuth();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showBugReport, setShowBugReport] = useState(false);
  const [bugMessage, setBugMessage] = useState('');
  const [submittingBug, setSubmittingBug] = useState(false);
  const [bugError, setBugError] = useState(null);
  const [bugSuccess, setBugSuccess] = useState(false);
  const [showChangeUsername, setShowChangeUsername] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [changingUsername, setChangingUsername] = useState(false);
  const [usernameError, setUsernameError] = useState(null);

  useEffect(() => {
    if (isAuthenticated && token) {
      refreshUser();
    }
  }, [isAuthenticated, token, refreshUser]);

  const handleSignOut = () => {
    logout();
    navigate('/');
  };

  const handleChangeUsername = async () => {
    if (!newUsername.trim()) {
      setUsernameError('Please enter a username');
      return;
    }

    try {
      setChangingUsername(true);
      setUsernameError(null);

      const response = await fetch(`${API_URL}/auth/username`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ username: newUsername.trim() })
      });

      // Check if response is JSON before parsing
      const contentType = response.headers.get('content-type');
      let data;
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        console.error('Non-JSON response:', text);
        throw new Error(`Server error: ${response.status} ${response.statusText}`);
      }

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update username');
      }

      // Update user context with new token and user data
      if (data.token && data.user) {
        updateToken(data.token);
        localStorage.setItem('nightfall_token', data.token);
        localStorage.setItem('nightfall_user', JSON.stringify(data.user));
        await refreshUser();
      }

      setShowChangeUsername(false);
      setNewUsername('');
      setUsernameError(null);
    } catch (err) {
      console.error('Error changing username:', err);
      setUsernameError(err.message || 'Failed to update username');
    } finally {
      setChangingUsername(false);
    }
  };

  const handleDeleteAccount = async () => {
    // TODO: Implement delete account API endpoint
    setDeleting(true);
    alert('Delete account functionality coming soon');
    setDeleting(false);
    setShowDeleteConfirm(false);
  };

  const handleSubmitBugReport = async () => {
    if (!bugMessage.trim()) {
      setBugError('Please enter a message');
      return;
    }

    if (bugMessage.length > 2000) {
      setBugError('Message is too long (max 2000 characters)');
      return;
    }

    try {
      setSubmittingBug(true);
      setBugError(null);
      setBugSuccess(false);

      const response = await fetch(`${API_URL}/auth/bug-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ message: bugMessage.trim() })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit bug report');
      }

      setBugSuccess(true);
      setBugMessage('');
      
      // Close modal after 2 seconds
      setTimeout(() => {
        setShowBugReport(false);
        setBugSuccess(false);
      }, 2000);
    } catch (err) {
      console.error('Error submitting bug report:', err);
      setBugError(err.message || 'Failed to submit bug report');
    } finally {
      setSubmittingBug(false);
    }
  };

  if (loading) {
    return (
      <main className="p-2 lg:p-5 pt-0 flex flex-col w-full max-w-full overflow-x-hidden">
        <div className="text-white text-center py-20">Loading...</div>
      </main>
    );
  }

  if (!isAuthenticated) {
    return (
      <main className="p-2 lg:p-5 pt-0 flex flex-col w-full max-w-full overflow-x-hidden">
        <style>{`
          .account-align {
            margin-top: calc(5.75rem + 0.5rem - 6rem);
          }
          @media (min-width: 768px) {
            .account-align {
              margin-top: calc(5.75rem + 1.25rem - 8rem);
            }
          }
        `}</style>
        <div className="account-align w-full">
          <div className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-3xl p-6 lg:p-8 shadow-xl">
            <h1 className="font-semibold text-2xl lg:text-3xl text-white mb-6">Account</h1>
            <div className="bg-dark-surface/50 border border-dark-border rounded-2xl p-8 text-center">
              <svg className="w-16 h-16 mx-auto mb-4 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <p className="text-white/60 mb-6">You are not signed in</p>
              <button
                onClick={() => navigate('/signin')}
                className="px-8 py-3 bg-accent-blue/20 border border-accent-blue/30 rounded-xl text-accent-blue font-medium hover:bg-accent-blue/30 hover:border-accent-blue/40 transition-all duration-200"
              >
                Sign In
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main className="p-2 lg:p-5 pt-0 flex flex-col w-full max-w-full overflow-x-hidden">
      <style>{`
        .account-align {
          margin-top: calc(5.75rem + 0.5rem - 6rem);
        }
        @media (min-width: 768px) {
          .account-align {
            margin-top: calc(5.75rem + 1.25rem - 8rem);
          }
        }
      `}</style>

      <div className="account-align w-full mb-6">
        <h2 className="font-semibold text-3xl lg:text-4xl text-white">Account</h2>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 w-full">
        {/* Column 1: Account Info */}
        <div className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-3xl p-6 lg:p-8 shadow-xl">
          <div className="mb-6 pb-4 border-b border-dark-border">
            <h3 className="font-semibold text-xl lg:text-2xl text-white">Account Information</h3>
          </div>

          <div className="space-y-6">
            {/* Username */}
            <div>
              <label className="block text-xs font-medium text-white/50 mb-4 lg:mb-6 uppercase tracking-wider">Username</label>
              <div className="text-white text-xl lg:text-2xl font-semibold">
                {user?.username || 'N/A'}
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-xs font-medium text-white/50 mb-4 lg:mb-6 uppercase tracking-wider">Email Address</label>
              <div className="text-white/90 text-base lg:text-lg">
                {user?.email || 'N/A'}
              </div>
            </div>
          </div>
        </div>

        {/* Column 2: Utilities */}
        <div className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-3xl p-6 lg:p-8 shadow-xl">
          <div className="mb-6 pb-4 border-b border-dark-border">
            <h3 className="font-semibold text-xl lg:text-2xl text-white">Utilities</h3>
          </div>

          <div className="flex flex-col gap-3">
            <button
              onClick={() => {
                setShowChangeUsername(true);
                setNewUsername(user?.username || '');
                setUsernameError(null);
              }}
              className="w-full px-5 py-3.5 bg-accent-blue/10 border border-accent-blue/30 rounded-xl text-accent-blue text-sm font-semibold hover:bg-accent-blue/20 hover:border-accent-blue/50 transition-all duration-200 flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Change Username
            </button>
            <button
              onClick={() => setShowBugReport(true)}
              className="w-full px-5 py-3.5 bg-yellow-600/10 border border-yellow-600/30 rounded-xl text-yellow-400 text-sm font-semibold hover:bg-yellow-600/20 hover:border-yellow-600/50 transition-all duration-200 flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              Support
            </button>
            <button
              onClick={handleSignOut}
              className="w-full px-5 py-3.5 bg-orange-600/10 border border-orange-600/30 rounded-xl text-orange-400 text-sm font-semibold hover:bg-orange-600/20 hover:border-orange-600/50 transition-all duration-200 flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Sign Out
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="w-full px-5 py-3.5 bg-red-600/10 border border-red-600/30 rounded-xl text-red-400 text-sm font-semibold hover:bg-red-600/20 hover:border-red-600/50 transition-all duration-200 flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete Account
            </button>
          </div>
        </div>
      </div>

      <div className="w-full lg:w-1/2 mt-6 lg:mt-8">
        <PaymentMethods />
      </div>

      <Footer />

      {/* Change Username Modal */}
      {showChangeUsername && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-5" onClick={() => {
          if (!changingUsername) {
            setShowChangeUsername(false);
            setNewUsername('');
            setUsernameError(null);
          }
        }}>
          <div 
            className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-3xl overflow-hidden max-w-lg w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-8">
              <div className="mb-6 flex justify-center">
                <div className="w-16 h-16 rounded-full bg-accent-blue/20 border-2 border-accent-blue/50 flex items-center justify-center">
                  <svg className="w-8 h-8 text-accent-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-white mb-2 text-center">Change Username</h2>
              <p className="text-white/60 mb-6 text-center text-sm">
                Choose a new username. It can only contain letters, numbers, and underscores.
              </p>

              {usernameError && (
                <div className="mb-4 bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-red-400 text-sm">
                  {usernameError}
                </div>
              )}

              <div className="mb-6">
                <label className="block text-xs font-medium text-white/50 mb-3 uppercase tracking-wider">
                  New Username
                </label>
                <input
                  type="text"
                  value={newUsername}
                  onChange={(e) => {
                    setNewUsername(e.target.value);
                    setUsernameError(null);
                  }}
                  placeholder="Enter new username"
                  className="w-full px-4 py-3 bg-dark-bg/50 border border-dark-border rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-accent-blue/50 focus:ring-2 focus:ring-accent-blue/20 transition-all"
                  disabled={changingUsername}
                  maxLength={50}
                  pattern="[a-zA-Z0-9_]+"
                />
                <div className="mt-2 text-xs text-white/40">
                  Current: <span className="text-white/60">{user?.username}</span>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowChangeUsername(false);
                    setNewUsername('');
                    setUsernameError(null);
                  }}
                  disabled={changingUsername}
                  className="flex-1 py-3 px-6 rounded-xl font-semibold transition-all duration-200 bg-dark-surface/50 border border-dark-border text-white hover:bg-dark-surface/70 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleChangeUsername}
                  disabled={changingUsername || !newUsername.trim() || newUsername.trim() === user?.username}
                  className="flex-1 py-3 px-6 rounded-xl font-semibold transition-all duration-200 bg-accent-blue text-white hover:bg-accent-blue/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {changingUsername ? 'Updating...' : 'Update Username'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bug Report Modal */}
      {showBugReport && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-5" onClick={() => {
          if (!submittingBug) {
            setShowBugReport(false);
            setBugMessage('');
            setBugError(null);
            setBugSuccess(false);
          }
        }}>
          <div 
            className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-3xl overflow-hidden max-w-lg w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-8">
              <div className="mb-6 flex justify-center">
                <div className="w-16 h-16 rounded-full bg-yellow-500/20 border-2 border-yellow-500/50 flex items-center justify-center">
                  <svg className="w-8 h-8 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-white mb-2 text-center">Support & Bug Reports</h2>
              <p className="text-white/60 mb-6 text-center text-sm">
                Need help or found a bug? Contact our support team! We'll get back to you as soon as possible.
              </p>

              {bugError && (
                <div className="mb-4 bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-red-400 text-sm">
                  {bugError}
                </div>
              )}

              {bugSuccess && (
                <div className="mb-4 bg-green-500/10 border border-green-500/30 rounded-xl p-3 text-green-400 text-sm">
                  Message sent successfully! Our support team will get back to you soon. Thank you!
                </div>
              )}

              <div className="mb-6">
                <label className="block text-xs font-medium text-white/50 mb-3 uppercase tracking-wider">
                  Your message
                </label>
                <textarea
                  value={bugMessage}
                  onChange={(e) => {
                    setBugMessage(e.target.value);
                    setBugError(null);
                  }}
                  placeholder="Describe your issue, bug, or question. Include as much detail as possible to help us assist you..."
                  className="w-full h-32 px-4 py-3 bg-dark-bg/50 border border-dark-border rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-accent-blue/50 focus:ring-2 focus:ring-accent-blue/20 transition-all resize-none"
                  disabled={submittingBug || bugSuccess}
                  maxLength={2000}
                />
                <div className="mt-2 text-right text-xs text-white/40">
                  {bugMessage.length}/2000 characters
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowBugReport(false);
                    setBugMessage('');
                    setBugError(null);
                    setBugSuccess(false);
                  }}
                  disabled={submittingBug}
                  className="flex-1 py-3 px-6 rounded-xl font-semibold transition-all duration-200 bg-dark-surface/50 border border-dark-border text-white hover:bg-dark-surface/70 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitBugReport}
                  disabled={submittingBug || bugSuccess || !bugMessage.trim()}
                  className="flex-1 py-3 px-6 rounded-xl font-semibold transition-all duration-200 bg-yellow-600 text-white hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submittingBug ? 'Sending...' : bugSuccess ? 'Sent!' : 'Send Message'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-5" onClick={() => setShowDeleteConfirm(false)}>
          <div 
            className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-3xl overflow-hidden max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-8">
              <div className="mb-6 flex justify-center">
                <div className="w-16 h-16 rounded-full bg-red-500/20 border-2 border-red-500/50 flex items-center justify-center">
                  <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-white mb-2 text-center">Delete Account</h2>
              <p className="text-white/60 mb-6 text-center">
                Are you sure you want to delete your account? This action cannot be undone. All your games, shards, and data will be permanently deleted.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                  className="flex-1 py-3 px-6 rounded-xl font-semibold transition-all duration-200 bg-dark-surface/50 border border-dark-border text-white hover:bg-dark-surface/70 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleting}
                  className="flex-1 py-3 px-6 rounded-xl font-semibold transition-all duration-200 bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleting ? 'Deleting...' : 'Delete Account'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export default Account;
